import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { createUseStyles } from "react-jss";
import { useSwipeable } from "react-swipeable";
import { useAtom } from "jotai";
import hsk from "../data/charactersList.json";
import CompactCardGrid from "./CompactCardGrid";
import { answeredState } from "../helpers/recoil";
import { getCardId } from "../helpers/cardId";
import {
  defaultDetailsOptions,
  getGridChunkDefault,
  getLatestTestResult,
  getStudyChunkDefault,
  loadSession,
  mobileDefaults,
  recordCardResult,
  recordCompletedTest,
  saveSession,
  SavedTestResult
} from "../helpers/storage";
import { AnswerRecord, Details, HskEntry, ViewMode } from "../helpers/types";
import { filterCardsByQuery } from "../helpers/search";
import { hapticTap } from "../helpers/haptics";
import CardNav from "./CardNav";
import DeckBrowseList from "./DeckBrowseList";
import DeckSearchBar from "./DeckSearchBar";
import Flashcard from "./Flashcard";
import MobileBrowsePagination from "./MobileBrowsePagination";
import MobileTabBar, { MobileTab } from "./MobileTabBar";
import MorePage from "./MorePage";
import Menu from "./Menu";
import StudyHeader, { FlipAllButton } from "./StudyHeader";
import StudyCardSlide, { useStudyCardSlide } from "./StudyCardSlide";
import TestSetup from "./TestSetup";
import ToggleMenuButton from "./ToggleMenuButton";
import ViewModeToggle from "./ViewModeToggle";

function shuffle(array: HskEntry[]) {
  const copy = [...array];
  let currentIndex = copy.length;

  while (currentIndex !== 0) {
    const randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;
    [copy[currentIndex], copy[randomIndex]] = [copy[randomIndex], copy[currentIndex]];
  }

  return copy;
}

const getCardsPerPage = (
  isTestMode: boolean,
  chunkLength: number,
  maxChunkLength: number,
  deckSize: number
) => {
  if (isTestMode) {
    return 1;
  }

  if (chunkLength === 0) {
    return maxChunkLength;
  }
  return Math.max(1, Math.min(chunkLength, deckSize || 1));
};

const SWIPE_THRESHOLD = 50;

const handleHorizontalPageSwipe = (
  eventData: { deltaX: number; deltaY: number; dir: string },
  onPreviousPage: () => void,
  onNextPage: () => void
) => {
  const absX = Math.abs(eventData.deltaX);
  const absY = Math.abs(eventData.deltaY);
  if (absX < SWIPE_THRESHOLD || absX <= absY) {
    return;
  }
  if (eventData.dir === "Left") {
    onNextPage();
  } else if (eventData.dir === "Right") {
    onPreviousPage();
  }
};

const useStyles = createUseStyles({
  mobileStudy: {
    display: "flex",
    flexDirection: "column",
    height: "100dvh",
    maxHeight: "100dvh",
    overflow: "hidden",
    "--mobile-tab-bar-height": "calc(56px + env(safe-area-inset-bottom, 0px))"
  },
  mobileContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "12px",
    minHeight: 0,
    overflowY: "auto"
  },
  mobileContentStudy: {
    paddingBottom: 0,
    overflow: "hidden"
  },
  mobileContentTest: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
    padding: "8px 12px"
  },
  mobileContentSetup: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
    padding: 0
  },
  mobileContentBrowse: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
    padding: 0
  },
  mobileBrowseScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "44px 12px 16px"
  },
  mobilePagedScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "12px 12px 16px"
  },
  mobileBottomChrome: {
    flexShrink: 0,
    position: "sticky",
    bottom: 0,
    zIndex: 20,
    backgroundColor: "#1e2229"
  },
  finishTestBar: {
    flexShrink: 0,
    padding: "8px 12px",
    borderBottom: "1px solid #404754",
    backgroundColor: "#1e2229"
  },
  browsePane: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden"
  },
  browseControls: {
    position: "absolute",
    top: 8,
    right: 12,
    zIndex: 6,
    display: "flex",
    alignItems: "center",
    gap: 6
  },
  browseActionsToggle: {
    width: "32px !important",
    height: "32px !important",
    borderRadius: "8px !important",
    border: "1px solid #404754 !important",
    backgroundColor: "#1e2229 !important",
    color: "#94a3b8 !important",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.24)",
    "&:hover": {
      backgroundColor: "#2c3139 !important",
      color: "#e2e8f0 !important"
    }
  },
  studySwipeArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    width: "100%"
  },
  mobileContentGrid: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden",
    padding: 0
  },
  gridViewWrap: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    width: "100%",
    overflowX: "clip",
    boxSizing: "border-box"
  },
  gridSwipeArea: {
    width: "100%"
  },
  desktopLayout: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "100vw",
    minHeight: "100dvh",
    overflowX: "clip",
    boxSizing: "border-box"
  },
  desktopLayoutPaginated: {
    paddingBottom: 96
  },
  desktopGridSection: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    width: "100%",
    overflowX: "clip",
    boxSizing: "border-box"
  },
  desktopStudyGrid: {
    display: "grid",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(200px, 100%), 1fr))",
    gap: 16,
    "& > *": {
      minWidth: 0
    }
  },
  desktopToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    padding: "8px 12px"
  },
  desktopTestShell: {
    display: "flex",
    justifyContent: "center",
    minHeight: "100dvh",
    backgroundColor: "#1e2229"
  },
  desktopTestArea: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100dvh",
    maxHeight: "100dvh",
    overflow: "hidden",
    boxSizing: "border-box",
    backgroundColor: "#282c34",
    borderLeft: "1px solid #404754",
    borderRight: "1px solid #404754"
  }
});

const getInitialState = (isMobile: boolean) => {
  const mobileSeed = isMobile ? mobileDefaults : {};
  const studyDefault = getStudyChunkDefault(isMobile);
  const gridDefault = getGridChunkDefault(isMobile);

  return {
    filteredLevels: mobileSeed.filteredLevels ?? [1],
    cardAmount: mobileSeed.cardAmount ?? 0,
    studyChunkLength: studyDefault,
    gridChunkLength: gridDefault,
    currentChunk: 0,
    cardIndex: 0,
    detailsOptions: mobileSeed.detailsOptions ?? defaultDetailsOptions,
    shuffled: false,
    isTestMode: false,
    viewMode: "study" as ViewMode,
    answeredList: [] as AnswerRecord[]
  };
};

export const CardList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const initial = useMemo(() => getInitialState(isMobile), [isMobile]);

  const [answeredList, setAnsweredList] = useAtom(answeredState);
  const [maxCards, setMaxCards] = useState(0);
  const [isFlippedCount, setIsFlippedCount] = useState(0);
  const [isRevealedCount, setIsRevealedCount] = useState(0);
  const [flippedByOrder, setFlippedByOrder] = useState<Record<number, boolean>>({});
  const [filteredLevels, setFilteredLevels] = useState(initial.filteredLevels);
  const [isTestMode, setIsTestMode] = useState(initial.isTestMode);
  const [viewMode, setViewMode] = useState<ViewMode>(
    initial.isTestMode ? "study" : initial.viewMode
  );
  const [shuffled, setShuffled] = useState(initial.shuffled);
  const [shuffledCount, setShuffledCount] = useState(0);
  const [cards, setCards] = useState<HskEntry[][]>([]);
  const [cardAmount, setCardAmount] = useState(initial.cardAmount);
  const [studyChunkLength, setStudyChunkLength] = useState(initial.studyChunkLength);
  const [gridChunkLength, setGridChunkLength] = useState(initial.gridChunkLength);
  const [currentChunk, setCurrentChunk] = useState(initial.currentChunk);
  const [cardIndex, setCardIndex] = useState(initial.cardIndex);
  const [detailsOptions, setDetailsOptions] = useState<Details>(initial.detailsOptions);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showBrowseActions, setShowBrowseActions] = useState(true);
  const [mobileTab, setMobileTab] = useState<MobileTab>(initial.isTestMode ? "test" : "study");
  const [lastTestResult, setLastTestResult] = useState<SavedTestResult | null>(null);
  const [customDeck, setCustomDeck] = useState<HskEntry[] | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const currentChunkRef = useRef(currentChunk);
  const pageCountRef = useRef(1);
  const skipFilterReset = useRef(true);
  const skipPageReset = useRef(true);
  const skipTestModeSideEffects = useRef(true);
  const skipStatsRecord = useRef(true);
  const previousAnswersRef = useRef<AnswerRecord[]>([]);

  const handleCardFlipped = useCallback((order: number, flipped: boolean) => {
    setFlippedByOrder((prev) => (prev[order] === flipped ? prev : { ...prev, [order]: flipped }));
  }, []);

  const maxChunkLength = 100;
  const classes = useStyles();
  const isGridView = viewMode === "grid";
  const chunkLength = isGridView ? gridChunkLength : studyChunkLength;

  const cardByOrder = useMemo(() => {
    const map = new Map<number, HskEntry>();
    (hsk as HskEntry[]).forEach((entry) => {
      map.set(entry.order, entry);
    });
    return map;
  }, []);

  const persistLastTest = () => {
    const testCards = cards.flat();
    if (testCards.length === 0) {
      return;
    }
    const answers = answeredList.map((entry) => ({ order: entry.order, answer: entry.answer }));
    const levels = [...new Set(testCards.map((card) => card.level))].sort((a, b) => a - b);
    const wasCustom = Boolean(customDeck && customDeck.length > 0);
    const result: SavedTestResult = {
      cards: testCards,
      answers,
      finishedAt: Date.now(),
      filteredLevels: levels,
      shuffled: wasCustom ? false : shuffled
    };
    setLastTestResult(result);
    void recordCompletedTest({
      cards: testCards,
      answers,
      filteredLevels: levels,
      cardAmount: testCards.length,
      shuffled: wasCustom ? false : shuffled
    }).then((saved) => {
      setLastTestResult(saved);
    });
  };

  const finishTest = () => {
    persistLastTest();
    setCustomDeck(null);
    setIsTestMode(false);
  };

  const startCustomTest = (deck: HskEntry[]) => {
    if (deck.length === 0) {
      return;
    }
    if (isTestMode) {
      persistLastTest();
    }
    setCustomDeck(deck);
    setCurrentChunk(0);
    setCardIndex(0);
    setIsTestMode(true);
    setMobileTab("test");
  };

  const startFilteredTest = () => {
    setCustomDeck(null);
    setIsTestMode(true);
  };

  const selectMobileTab = (tab: MobileTab) => {
    hapticTap();
    setMobileTab(tab);
    if (tab === "study" && isTestMode) {
      finishTest();
    }
  };

  const handleSetIsTestMode = (nextTestMode: boolean) => {
    if (!nextTestMode && isTestMode) {
      persistLastTest();
      setCustomDeck(null);
    }
    if (nextTestMode) {
      setCustomDeck(null);
    }
    setIsTestMode(nextTestMode);
    setMobileTab(nextTestMode ? "test" : "study");
  };

  const setChunkLength = (value: number) => {
    if (isGridView) {
      setGridChunkLength(value);
      return;
    }
    setStudyChunkLength(value);
  };

  const menuProps = {
    chunkLength,
    setChunkLength,
    maxChunkLength,
    cardAmount,
    detailsOptions,
    filteredLevels,
    setFilteredLevels,
    setDetailsOptions,
    setCardAmount,
    maxCards,
    isTestMode,
    setIsTestMode: handleSetIsTestMode,
    setShuffled,
    setShuffledCount,
    shuffledCount,
    shuffled,
    isMobile,
    viewMode,
    setViewMode
  };

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      const saved = await loadSession();
      const latestTest = await getLatestTestResult();
      if (cancelled) {
        return;
      }

      if (saved) {
        const savedTestMode = saved.isTestMode ?? false;
        let studyChunkLength =
          saved.studyChunkLength ?? saved.chunkLength ?? initial.studyChunkLength;
        if (isMobile && !savedTestMode && studyChunkLength === 1) {
          studyChunkLength = getStudyChunkDefault(true);
        }

        setFilteredLevels(saved.filteredLevels ?? initial.filteredLevels);
        setCardAmount(saved.cardAmount ?? initial.cardAmount);
        setStudyChunkLength(studyChunkLength);
        setGridChunkLength(saved.gridChunkLength ?? initial.gridChunkLength);
        setCurrentChunk(saved.currentChunk ?? 0);
        setCardIndex(saved.cardIndex ?? 0);
        setDetailsOptions(saved.detailsOptions ?? initial.detailsOptions);
        setShuffled(saved.shuffled ?? false);
        setIsTestMode(savedTestMode);
        setViewMode(savedTestMode ? "study" : ((saved.viewMode ?? "study") as ViewMode));
        setMobileTab(savedTestMode ? "test" : "study");
        setAnsweredList(saved.answeredList ?? []);
      }

      if (latestTest) {
        setLastTestResult(latestTest);
      }
      setHydrated(true);
    };

    void hydrate();
    return () => {
      cancelled = true;
    };
    // Hydrate once from IndexedDB; isMobile is read from the first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    if (skipTestModeSideEffects.current) {
      skipTestModeSideEffects.current = false;
      return;
    }
    if (isTestMode) {
      setAnsweredList([]);
      setViewMode("study");
      setStudyChunkLength(1);
      return;
    }
    if (!isMobile) {
      setStudyChunkLength((prev) => (prev === 1 ? 20 : prev));
    } else {
      setStudyChunkLength((prev) => (prev === 1 ? getStudyChunkDefault(true) : prev));
    }
  }, [hydrated, isTestMode, isMobile, setAnsweredList]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    if (isTestMode || skipStatsRecord.current) {
      if (skipStatsRecord.current && !isTestMode) {
        skipStatsRecord.current = false;
      }
      previousAnswersRef.current = answeredList;
      return;
    }

    const previousByOrder = new Map(
      previousAnswersRef.current.map((entry) => [entry.order, entry.answer])
    );
    previousAnswersRef.current = answeredList;

    answeredList.forEach((entry) => {
      if (entry.answer !== true && entry.answer !== false) {
        return;
      }
      if (previousByOrder.get(entry.order) === entry.answer) {
        return;
      }
      const card = cardByOrder.get(entry.order);
      if (!card) {
        return;
      }
      void recordCardResult(getCardId(card), entry.answer);
    });
  }, [answeredList, cardByOrder, hydrated, isTestMode]);

  useEffect(() => {
    const filteredCards = filterCardsByQuery(
      hsk.filter((hskEntry: HskEntry) => filteredLevels.includes(hskEntry.level)),
      searchQuery
    );
    setMaxCards(filteredCards.length);
    const limitedCards = cardAmount === 0 ? filteredCards : filteredCards.slice(0, cardAmount);
    const sourceCards = customDeck && customDeck.length > 0 ? customDeck : limitedCards;
    const shuffledCards =
      customDeck && customDeck.length > 0 ? sourceCards : shuffled ? shuffle(sourceCards) : sourceCards;
    const evalChunkLength = getCardsPerPage(
      isTestMode,
      chunkLength,
      maxChunkLength,
      shuffledCards.length
    );

    const chunks = shuffledCards.reduce<HskEntry[][]>((acc, key, index) => {
      if (index % evalChunkLength === 0) {
        acc.push([key]);
      } else {
        acc[acc.length - 1].push(key);
      }
      return acc;
    }, []);

    setCards(chunks.length ? chunks : [[]]);
    setCurrentChunk((prev) => Math.min(prev, Math.max(chunks.length - 1, 0)));
  }, [
    filteredLevels,
    shuffled,
    shuffledCount,
    cardAmount,
    chunkLength,
    isTestMode,
    viewMode,
    studyChunkLength,
    gridChunkLength,
    searchQuery,
    customDeck
  ]);

  useEffect(() => {
    if (skipPageReset.current) {
      skipPageReset.current = false;
      return;
    }
    setCurrentChunk(0);
    setCardIndex(0);
  }, [chunkLength, viewMode, cardAmount, studyChunkLength, gridChunkLength]);

  useEffect(() => {
    if (skipFilterReset.current) {
      skipFilterReset.current = false;
      return;
    }
    setCurrentChunk(0);
    setCardIndex(0);
  }, [filteredLevels, shuffled, shuffledCount, cardAmount, searchQuery]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    void saveSession({
      filteredLevels,
      cardAmount,
      studyChunkLength,
      gridChunkLength,
      currentChunk,
      cardIndex,
      detailsOptions,
      shuffled,
      isTestMode,
      viewMode,
      answeredList
    });
  }, [
    hydrated,
    filteredLevels,
    cardAmount,
    studyChunkLength,
    gridChunkLength,
    currentChunk,
    cardIndex,
    detailsOptions,
    shuffled,
    isTestMode,
    viewMode,
    answeredList
  ]);

  const currentChunkCards = cards[currentChunk] ?? [];
  const visibleCardsAllReversed =
    currentChunkCards.length > 0 &&
    currentChunkCards.every((card) => flippedByOrder[card.order] === true);
  const activeCard = currentChunkCards[cardIndex];
  const totalCardsInSession = cards.reduce((sum, chunk) => sum + chunk.length, 0);
  const globalCardPosition =
    cards.slice(0, currentChunk).reduce((sum, chunk) => sum + chunk.length, 0) + cardIndex + 1;
  const totalPages = Math.max(cards.length, 1);
  const cardsBeforePage = cards.slice(0, currentChunk).reduce((sum, chunk) => sum + chunk.length, 0);
  const cardRangeStart = currentChunkCards.length > 0 ? cardsBeforePage + 1 : undefined;
  const cardRangeEnd =
    currentChunkCards.length > 0 ? cardsBeforePage + currentChunkCards.length : undefined;
  const cardsPerPage = getCardsPerPage(isTestMode, chunkLength, maxChunkLength, totalCardsInSession);
  const isMoreTab = isMobile && mobileTab === "more";
  const isTestSetup = isMobile && mobileTab === "test" && !isTestMode;
  const isTestRunning = isMobile && mobileTab === "test" && isTestMode;
  const isMobileStudyBrowse = isMobile && mobileTab === "study" && !isGridView;
  const isMobileSingleCardStudy = isTestRunning;
  const handleFlipAll = () => {
    hapticTap();
    if (visibleCardsAllReversed) {
      setIsRevealedCount((count) => count + 1);
    } else {
      setIsFlippedCount((count) => count + 1);
    }
  };
  const autoAdvanceOnAnswer =
    isTestMode || (!isGridView && !isMobile && cardsPerPage === 1);
  const canGoNext = globalCardPosition < totalCardsInSession;

  const canGoPrevious = globalCardPosition > 1;

  const goToPreviousCard = () => {
    if (cardIndex > 0) {
      setCardIndex(cardIndex - 1);
      return;
    }
    if (currentChunk > 0) {
      const prevChunk = cards[currentChunk - 1] ?? [];
      setCurrentChunk(currentChunk - 1);
      setCardIndex(Math.max(prevChunk.length - 1, 0));
    }
  };

  const goToNextCard = () => {
    if (cardIndex < currentChunkCards.length - 1) {
      setCardIndex(cardIndex + 1);
      return;
    }
    if (currentChunk < cards.length - 1) {
      setCurrentChunk(currentChunk + 1);
      setCardIndex(0);
    }
  };

  const { slidePhase, slideDirection, transitionEnabled, animateToNextCard, animateToPreviousCard, animateNavigation } =
    useStudyCardSlide(goToNextCard, goToPreviousCard, canGoNext, canGoPrevious);

  useEffect(() => {
    currentChunkRef.current = currentChunk;
  }, [currentChunk]);

  useEffect(() => {
    pageCountRef.current = Math.max(cards.length, 1);
  }, [cards.length]);

  const handlePageChange = (page: number) => {
    const targetChunk = page - 1;
    setCurrentChunk(targetChunk);
    setCardIndex(0);
    currentChunkRef.current = targetChunk;
  };

  const stepPage = (delta: number) => {
    animateNavigation(delta > 0 ? "forward" : "backward", () => {
      setCurrentChunk((prev) => {
        const maxChunk = Math.max(pageCountRef.current - 1, 0);
        const next = prev + delta;
        if (next < 0 || next > maxChunk) {
          return prev;
        }
        currentChunkRef.current = next;
        return next;
      });
      setCardIndex(0);
    });
  };

  const handleAnimatedPageChange = (page: number) => {
    animateNavigation(
      () => (page - 1 > currentChunkRef.current ? "forward" : "backward"),
      () => handlePageChange(page)
    );
  };

  const goToPreviousPage = () => {
    if (currentChunkRef.current <= 0) {
      return;
    }
    hapticTap();
    stepPage(-1);
  };

  const goToNextPage = () => {
    if (currentChunkRef.current >= pageCountRef.current - 1) {
      return;
    }
    hapticTap();
    stepPage(1);
  };

  const wrapInPageSlide = (content: ReactNode) => {
    if (cards.length <= 1) {
      return content;
    }
    return (
      <StudyCardSlide
        slidePhase={slidePhase}
        slideDirection={slideDirection}
        transitionEnabled={transitionEnabled}>
        {content}
      </StudyCardSlide>
    );
  };

  const renderFlashcard = (card: HskEntry, mobile = false, compact = false, nav?: ReactNode) => (
    <Flashcard
      cardDetails={{
        level: card.level,
        charSimp: card.charSimp,
        charTrad: card.charTrad,
        freqRaw: card.freqRaw,
        freqPerc: card.freqPerc,
        pinyin: card.pinyin,
        meaning: card.meaning
      }}
      detailsOptions={detailsOptions}
      key={card.order}
      isFlippedCount={isFlippedCount}
      isRevealedCount={isRevealedCount}
      showClearButton={!autoAdvanceOnAnswer}
      order={card.order}
      isMobile={mobile}
      isCompact={compact}
      onFlippedChange={handleCardFlipped}
      onAnswerMarked={autoAdvanceOnAnswer ? animateToNextCard : undefined}
      slidePhase={autoAdvanceOnAnswer ? slidePhase : undefined}
      slideDirection={autoAdvanceOnAnswer ? slideDirection : undefined}
      transitionEnabled={autoAdvanceOnAnswer ? transitionEnabled : undefined}
      hasSwipeNavigation={autoAdvanceOnAnswer}
      nav={nav}
    />
  );

  const gridPageSwipeHandlers = useSwipeable({
    onSwiped: (eventData) => {
      if (cards.length <= 1) {
        return;
      }
      handleHorizontalPageSwipe(eventData, goToPreviousPage, goToNextPage);
    },
    preventScrollOnSwipe: false,
    trackMouse: !isMobile,
    delta: SWIPE_THRESHOLD
  });

  const studyCardSwipeHandlers = useSwipeable({
    onSwiped: (eventData) => {
      if (!autoAdvanceOnAnswer || isGridView) {
        return;
      }
      handleHorizontalPageSwipe(
        eventData,
        () => {
          if (canGoPrevious) {
            hapticTap();
            animateToPreviousCard();
          }
        },
        () => {
          if (canGoNext) {
            hapticTap();
            animateToNextCard();
          }
        }
      );
    },
    preventScrollOnSwipe: false,
    trackMouse: !isMobile || isTestMode,
    delta: SWIPE_THRESHOLD
  });

  const studyPageSwipeHandlers = useSwipeable({
    onSwiped: (eventData) => {
      if (isGridView || autoAdvanceOnAnswer || cards.length <= 1) {
        return;
      }
      handleHorizontalPageSwipe(eventData, goToPreviousPage, goToNextPage);
    },
    preventScrollOnSwipe: false,
    trackMouse: !isMobile,
    delta: SWIPE_THRESHOLD
  });

  const showBrowsePagination = isMobile
    ? mobileTab === "study" && (isGridView || isMobileStudyBrowse)
    : !isTestMode && (isGridView || cardsPerPage > 1);

  const browsePagination = showBrowsePagination ? (
    <MobileBrowsePagination
      page={currentChunk + 1}
      count={totalPages}
      cardRangeStart={cardRangeStart}
      cardRangeEnd={cardRangeEnd}
      onPrevious={() => {
        hapticTap();
        stepPage(-1);
      }}
      onNext={() => {
        hapticTap();
        stepPage(1);
      }}
      onChange={handleAnimatedPageChange}
      stacked={isMobile}
    />
  ) : null;

  const renderMobileGrid = () => (
    <div
      className={classes.mobilePagedScroll}
      {...(cards.length > 1 ? gridPageSwipeHandlers : {})}>
      {wrapInPageSlide(renderCompactGrid(true))}
    </div>
  );

  const renderMobileStudyBrowse = () => (
    <div className={classes.browsePane}>
      <div className={classes.browseControls}>
        <FlipAllButton
          allReversed={visibleCardsAllReversed}
          onFlipAll={handleFlipAll}
          disabled={currentChunkCards.length === 0}
          className={classes.browseActionsToggle}
        />
        <IconButton
          className={classes.browseActionsToggle}
          size="small"
          aria-label={showBrowseActions ? "Hide mark buttons" : "Show mark buttons"}
          aria-expanded={showBrowseActions}
          onClick={() => {
            hapticTap();
            setShowBrowseActions((visible) => !visible);
          }}>
          {showBrowseActions ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ChevronLeftIcon fontSize="small" />
          )}
        </IconButton>
      </div>
      <div
        className={classes.mobileBrowseScroll}
        {...(cards.length > 1 ? studyPageSwipeHandlers : {})}>
        {currentChunkCards.length > 0 ? (
          wrapInPageSlide(
            <DeckBrowseList
              cards={currentChunkCards}
              detailsOptions={detailsOptions}
              isFlippedCount={isFlippedCount}
              isRevealedCount={isRevealedCount}
              showActions={showBrowseActions}
              onFlippedChange={handleCardFlipped}
            />
          )
        ) : (
          <Box sx={{ p: 3, color: "#94a3b8" }}>
            {searchQuery.trim() ? "No cards match your search." : "No cards match your filters."}
          </Box>
        )}
      </div>
    </div>
  );

  const renderCompactGrid = (mobile = false) => (
    <CompactCardGrid cards={currentChunkCards} isMobile={mobile} />
  );

  const gridView = (
    <div className={classes.gridViewWrap}>
      <div
        className={classes.gridSwipeArea}
        {...(cards.length > 1 ? gridPageSwipeHandlers : {})}>
        {wrapInPageSlide(renderCompactGrid(isMobile))}
      </div>
    </div>
  );

  const settingsDrawer = (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      variant="temporary"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          width: isMobile ? "100%" : 320,
          maxHeight: isMobile ? "85vh" : "100%",
          overflow: isMobile ? "hidden" : "auto",
          borderTopLeftRadius: isMobile ? 16 : 0,
          borderTopRightRadius: isMobile ? 16 : 0,
          backgroundColor: "#282c34",
          color: "#e2e8f0"
        }
      }}>
      <Menu {...menuProps} onClose={() => setDrawerOpen(false)} />
    </Drawer>
  );

  if (!hydrated) {
    return <Box sx={{ minHeight: "100dvh", backgroundColor: "#282c34" }} />;
  }

  const finishTestButton = (
    <Box className={classes.finishTestBar}>
      <Button
        variant="outlined"
        fullWidth
        onClick={finishTest}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: "#e2e8f0",
          borderColor: "#404754"
        }}>
        Finish test
      </Button>
    </Box>
  );

  const runningTestCard = activeCard ? (
    <div className={classes.studySwipeArea}>
      {renderFlashcard(
        activeCard,
        true,
        false,
        <CardNav
          cardPosition={globalCardPosition}
          totalCards={totalCardsInSession}
          onPrevious={autoAdvanceOnAnswer ? animateToPreviousCard : goToPreviousCard}
          onNext={autoAdvanceOnAnswer ? animateToNextCard : goToNextCard}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
        />
      )}
    </div>
  ) : (
    <Box sx={{ p: 3, color: "#94a3b8" }}>No cards match your filters.</Box>
  );

  if (isMobile) {
    return (
      <Box className={classes.mobileStudy}>
        <StudyHeader
          viewMode={viewMode}
          setViewMode={setViewMode}
          cardPosition={totalCardsInSession > 0 ? globalCardPosition : 0}
          totalCards={totalCardsInSession}
          onOpenMenu={() => setDrawerOpen(true)}
          isBrowseMode={mobileTab === "study" ? !isGridView : isTestSetup}
          showViewToggle={mobileTab === "study"}
          title={mobileTab === "more" ? "More" : isTestSetup ? "Test" : undefined}
          showCount={mobileTab !== "more"}
        />
        {isTestMode ? finishTestButton : null}
        {isMoreTab ? (
          <MorePage onStartCustomTest={startCustomTest} />
        ) : isTestSetup ? (
          <Box className={classes.mobileContentSetup}>
            <TestSetup
              filteredLevels={filteredLevels}
              setFilteredLevels={setFilteredLevels}
              cardAmount={cardAmount}
              setCardAmount={setCardAmount}
              maxCards={maxCards}
              detailsOptions={detailsOptions}
              setDetailsOptions={setDetailsOptions}
              shuffled={shuffled}
              onRandomize={() => {
                setShuffled(true);
                setShuffledCount(shuffledCount + 1);
              }}
              onReorder={() => setShuffled(false)}
              onStartTest={startFilteredTest}
              onStartCustomTest={startCustomTest}
              lastTestResult={lastTestResult}
            />
          </Box>
        ) : (
          <>
            {isMobileStudyBrowse ? (
              <DeckSearchBar value={searchQuery} onChange={setSearchQuery} />
            ) : null}
            <Box
              className={`${classes.mobileContent} ${
                isTestRunning
                  ? classes.mobileContentTest
                  : isGridView
                    ? classes.mobileContentGrid
                    : isMobileStudyBrowse
                      ? classes.mobileContentBrowse
                      : classes.mobileContentStudy
              }`}
              {...(isMobileSingleCardStudy && autoAdvanceOnAnswer ? studyCardSwipeHandlers : {})}>
              {isGridView && mobileTab === "study" ? (
                currentChunkCards.length > 0 ? (
                  renderMobileGrid()
                ) : (
                  <Box sx={{ p: 3, color: "#94a3b8" }}>No cards match your filters.</Box>
                )
              ) : isMobileStudyBrowse ? (
                renderMobileStudyBrowse()
              ) : isTestRunning || activeCard ? (
                runningTestCard
              ) : (
                <Box sx={{ p: 3, color: "#94a3b8" }}>No cards match your filters.</Box>
              )}
            </Box>
          </>
        )}
        <Box className={classes.mobileBottomChrome}>
          {browsePagination}
          <MobileTabBar active={mobileTab} onChange={selectMobileTab} />
        </Box>
        {settingsDrawer}
      </Box>
    );
  }

  if (isTestMode) {
    return (
      <Box className={classes.desktopTestShell}>
        <Box
          className={classes.desktopTestArea}
          sx={{ maxWidth: theme.breakpoints.values.md }}>
          <StudyHeader
            viewMode={viewMode}
            setViewMode={setViewMode}
            cardPosition={totalCardsInSession > 0 ? globalCardPosition : 0}
            totalCards={totalCardsInSession}
            onOpenMenu={() => setDrawerOpen(true)}
            isBrowseMode={false}
            showViewToggle={false}
            showCount
          />
          {finishTestButton}
          <Box
            className={`${classes.mobileContent} ${classes.mobileContentTest}`}
            {...(autoAdvanceOnAnswer ? studyCardSwipeHandlers : {})}>
            {runningTestCard}
          </Box>
          {settingsDrawer}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={`${classes.desktopLayout} ${
        showBrowsePagination ? classes.desktopLayoutPaginated : ""
      }`}>
      <Box className={classes.desktopToolbar}>
        <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />
        {!isGridView && !isTestMode ? (
          <FlipAllButton
            allReversed={visibleCardsAllReversed}
            onFlipAll={handleFlipAll}
            disabled={currentChunkCards.length === 0}
          />
        ) : null}
        <ToggleMenuButton handleDrawerToggle={() => setDrawerOpen(true)} isInDrawerIcon={false} />
      </Box>
      {isGridView ? (
        <Box className={classes.desktopGridSection} sx={{ p: 3 }}>
          {currentChunkCards.length > 0 ? (
            gridView
          ) : (
            <Box sx={{ p: 3, color: "#94a3b8" }}>No cards match your filters.</Box>
          )}
        </Box>
      ) : (
        <Box
          component="section"
          className={classes.desktopGridSection}
          sx={{ p: 3 }}
          {...(autoAdvanceOnAnswer
            ? studyCardSwipeHandlers
            : cards.length > 1
              ? studyPageSwipeHandlers
              : {})}>
          {currentChunkCards.length > 0 ? (
            <>
              {autoAdvanceOnAnswer ? (
                <Box className={classes.desktopStudyGrid}>
                  {currentChunkCards.map((card) => renderFlashcard(card))}
                </Box>
              ) : (
                wrapInPageSlide(
                  <Box className={classes.desktopStudyGrid}>
                    {currentChunkCards.map((card) => renderFlashcard(card))}
                  </Box>
                )
              )}
            </>
          ) : (
            <Box sx={{ p: 3, color: "#94a3b8" }}>No cards match your filters.</Box>
          )}
        </Box>
      )}
      {browsePagination}
      {settingsDrawer}
    </Box>
  );
};

export default CardList;
